/**
 * Team Inbox — module entry (Integration Plan §3: single export).
 *
 *   <TeamInbox/>      — the production workspace (auth + live data).
 *   <TeamInboxGate/>  — flag wrapper: settings/flags.teaminbox ON → the
 *                       new workspace · OFF (default) → the `fallback`
 *                       (today's Messages screen). The App.tsx route swap
 *                       is a MERGE-TIME one-liner (see MERGE_NOTES.md):
 *                         <Route path="/messages"
 *                           element={<TeamInboxGate fallback={<Messages/>}/>}/>
 *   <TeamInboxView/>  — the workspace with explicit wiring (the dev
 *                       harness mounts this with the fixture data source;
 *                       no Firebase/auth/router needed).
 *
 * Phase 2 wires the ownership surface: a `teamInboxAdmin · meta` poll
 * rides next to the existing list poll and merges ownerUid/claim/
 * escalation onto the rows (the legacy list callable predates V2 and
 * cannot carry them); the Assign menu, claim heartbeat, S6 settings and
 * the desktop-notification stop-gap (R-D5) all hang off that one poll.
 * Identity: the allocation engine writes STAFF ids (E001…), so "me" =
 * auth staffId (uid kept as a defensive alias).
 *
 * Everything lives inside src/features/teaminbox/ — no existing file is
 * modified (lane rule; integration diffs deferred to MERGE_NOTES.md).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { type AttachmentInput } from '../../lib/messagesStore';
import { Box, EmptyNote } from '../../components/ui';
import { subscribeTeamInboxFlag } from './lib/flags';
import {
  applyInboxFilter,
  inboxFilterCounts,
  liveDataSource,
  snippetForMessage,
  LIVE_POLL_MS,
  type InboxDataSource,
  type ThreadData,
} from './lib/inboxStore';
import { subscribeNumberRegistry, fallbackRegistry } from './lib/numbers';
import { can, mergeMeta } from './lib/ownership';
import {
  listenClients,
  resolveContactByWhatsApp,
  type LiveClient,
} from '../../lib/projectStore';
import { listenLeads } from '../../lib/leadStore';
import type { Lead } from '../../lib/leads';
import {
  adminScopeOf,
  desktopNotifier,
  loadPrefs,
  notificationPlan,
  quietHoursOf,
  savePrefs,
} from './lib/notify';
import type {
  CannedReply,
  InboxChannel,
  InboxConversation,
  InboxFilter,
  InboxMeta,
  InboxMessage,
  NumberRegistry,
  ThreadEvent,
} from './lib/types';
import {
  applyChannelFilter,
  buildReplyParams,
  draftToInboxConversation,
} from './lib/emailStore';
import { sendEmailReply, sendNewEmail } from './lib/emailSend';
import {
  assignDraftContact,
  buildWaTriageDraft,
  createContactFromForm,
  createLeadFromForm,
  promoteWaLeadFromTriage,
  linkEmailDraftToContact,
  linkEmailDraftToLead,
  linkIdentityToLead,
  linkWhatsAppToContact,
  listenEmailQueue,
  listenHeldEmails,
  markEmailRead,
  setTriageAction,
  unholdDraftContact,
  waTriageDraftId,
  type DraftContact,
  type TriageFormInput,
} from '../../lib/triageStore';
import {
  channelTotalCounts,
  rowMatchesInbox,
  inboxFilterOptions,
} from './lib/headerMenu';
import { visibleCanned } from './lib/sendPicker';
import InboxList from './screens/InboxList';
import Thread from './screens/Thread';
import EmailThread, { type SentReply, type TriageAction } from './screens/EmailThread';
import InboxSettings from './screens/InboxSettings';
import ComposeEmail from './components/ComposeEmail';
import ComposeWhatsApp from './components/ComposeWhatsApp';
import { startWaConversation } from './lib/waStart';
import ContactPicker from './components/ContactPicker';
import ForwardPicker from './components/ForwardPicker';
import { hasForwardableAttachment } from './components/bubbles';
import {
  runForwardMany,
  forwardManyErrorText,
  routeForward,
  type ForwardItem,
} from './lib/forwardMany';
import CreateContactForm, { type CreateMode } from './components/CreateContactForm';
import './styles/teaminbox.css';

/**
 * Build the transport-agnostic ForwardItem for ONE message. A message with a
 * forwardable attachment (image/document) carries its `attachment` (routed to
 * the file transport, bytes reused); every other forwardable message carries
 * its `text` (routed to a plain text send). The `fileName` label is the file
 * name for media, or a short quote of the text — used in progress + error copy.
 */
function toForwardItem(m: InboxMessage): ForwardItem {
  const media = hasForwardableAttachment(m);
  const body = (m.body || '').trim();
  const label =
    m.attachment?.fileName ||
    (body ? `“${body.slice(0, 30)}${body.length > 30 ? '…' : ''}”` : 'message');
  return {
    id: m.id,
    fileName: label,
    attachment: media ? m.attachment : null,
    text: media ? null : m.body || null,
  };
}

/* ───────────────────────── the workspace ───────────────────────── */

export function TeamInboxView({
  dataSource,
  myUid,
  role = 'staff',
  registrySource = subscribeNumberRegistry,
  emailSource = (cb, onErr) => listenEmailQueue(cb, onErr),
  heldEmailSource = (cb, onErr) => listenHeldEmails(cb, onErr),
  leadsSource = (cb, onErr) => listenLeads(cb, onErr),
  initialKey = null,
  onActiveChange,
  linkProject = true,
}: {
  dataSource: InboxDataSource;
  /** My owner-identity (staffId preferred — the engine writes staff ids). */
  myUid: string | null;
  role?: 'admin' | 'staff';
  /** Registry subscription (the harness passes a fixture-backed one). */
  registrySource?: (cb: (reg: NumberRegistry) => void) => () => void;
  /**
   * Email triage subscription (care@ → draft_contacts). Defaults to the live
   * Firestore listener; the dev harness passes a fixture-backed one so the
   * email channel previews without Firebase.
   */
  emailSource?: (
    cb: (items: DraftContact[]) => void,
    onError: (err?: unknown) => void,
  ) => () => void;
  /**
   * Held-email subscription (draft_contacts where triageAction:'hold'). Drives
   * the "Held" view + un-hold recovery so an accidental Hold is never a
   * dead-end. Defaults to the live listener; harness passes a fixture.
   */
  heldEmailSource?: (
    cb: (items: DraftContact[]) => void,
    onError: (err?: unknown) => void,
  ) => () => void;
  /**
   * Leads subscription — feeds the AD-039 joined-lead chip + the "Add to
   * existing → Leads" picker tab. Defaults to the live listenLeads; the dev
   * harness passes a fixture-backed one so the chip previews without Firebase.
   */
  leadsSource?: (
    cb: (leads: Lead[]) => void,
    onError: (err?: unknown) => void,
  ) => () => void;
  /** Deep-linked conversation key (?c=…) to open on mount. */
  initialKey?: string | null;
  /** Notified when the open conversation changes (URL sync upstream). */
  onActiveChange?: (key: string | null) => void;
  linkProject?: boolean;
}) {
  const [registry, setRegistry] = useState<NumberRegistry>(fallbackRegistry);
  const registryRef = useRef(registry);
  registryRef.current = registry;

  const [rows, setRows] = useState<InboxConversation[] | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  // Email-queue listener error, surfaced as a visible banner (was console-only,
  // which hid a needs-index failure that silently served a stale cache).
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [meta, setMeta] = useState<InboxMeta | null>(null);

  const [filter, setFilter] = useState<InboxFilter>('all');
  const [channel, setChannel] = useState<InboxChannel>('all');
  const [numberId, setNumberId] = useState<string>('all');
  const [search, setSearch] = useState('');

  // ── EMAIL channel (care@ triage records from draft_contacts) ──────
  const [emailDrafts, setEmailDrafts] = useState<DraftContact[]>([]);
  // Session-scoped read state — optimistic clear before the persisted `read`
  // flag (markEmailRead) round-trips. The persisted flag is what survives a
  // refresh; this just zeroes the badge instantly on open.
  const [openedEmailKeys, setOpenedEmailKeys] = useState<Set<string>>(
    () => new Set(),
  );
  // Held emails (triageAction:'hold') — a parked, recoverable lane. Shown only
  // when the "Held" toggle is on, so an accidental Hold is never a dead-end.
  const [heldDrafts, setHeldDrafts] = useState<DraftContact[]>([]);
  const [showHeld, setShowHeld] = useState(false);
  // Optimistic sent replies, keyed by email draftId, shown inline in the thread.
  const [sentReplies, setSentReplies] = useState<Record<string, SentReply[]>>(
    {},
  );
  const [replying, setReplying] = useState(false);
  const [replyErr, setReplyErr] = useState<string | null>(null);
  const [triageBusy, setTriageBusy] = useState(false);
  // "Add to existing" picker — open with the channel + identity (email/phone)
  // being linked; the WA phone is carried so a link from an unknown WA sender
  // (which has no draft) knows which number to attach.
  const [picker, setPicker] = useState<
    { channel: 'email' | 'whatsapp'; identity: string; phone: string | null } | null
  >(null);
  const [linkBusy, setLinkBusy] = useState(false);
  // Create Contact / Create Lead FORM (item 2). Carries the channel + the
  // identity to pre-fill (editable); the name starts empty (the bug fix).
  const [createForm, setCreateForm] = useState<{
    mode: CreateMode;
    channel: 'whatsapp' | 'email';
    phone: string;
    email: string;
  } | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const [composeErr, setComposeErr] = useState<string | null>(null);
  // New-WhatsApp composer (2026-08-02) — separate state from the email
  // composer so neither can affect the other.
  const [waComposeOpen, setWaComposeOpen] = useState(false);
  const [waComposeSending, setWaComposeSending] = useState(false);
  const [waComposeErr, setWaComposeErr] = useState<string | null>(null);
  const sentReplyCounter = useRef(0);

  const [activeKey, setActiveKey] = useState<string | null>(initialKey);
  const [thread, setThread] = useState<ThreadData | null>(null);
  const [events, setEvents] = useState<ThreadEvent[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [unreadAtOpen, setUnreadAtOpen] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Deep-link: /messages?settings=… opens Inbox Settings on mount (e.g. the
  // global Settings page's "Canned messages" row links here). Consume the
  // param once so a refresh doesn't re-open it.
  const [deepLinkParams, setDeepLinkParams] = useSearchParams();
  useEffect(() => {
    if (!deepLinkParams.get('settings')) return;
    setSettingsOpen(true);
    const next = new URLSearchParams(deepLinkParams);
    next.delete('settings');
    setDeepLinkParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 3b first-touch deep link: /messages?c=<key>&picker=1 lands on the
  // thread WITH the send picker (canned/templates) already open — the lead
  // record's "Send first touch" door. Consumed once (same pattern as
  // ?settings) so a refresh doesn't re-open it.
  // Vera MEDIUM-2 (2026-07-04): the flag is KEYED to the deep-linked
  // conversation (the ?c= value at consume time) and clears the moment a
  // DIFFERENT thread opens — previously it never reset, so every
  // subsequently opened thread auto-opened the picker.
  const [pickerForKey, setPickerForKey] = useState<string | null>(null);
  // Forward-attachment picker: the source message being forwarded (null =
  // closed) + an in-flight guard so a double-tap can't fire two sends.
  const [forwardMsg, setForwardMsg] = useState<InboxMessage | null>(null);
  const [forwardBusy, setForwardBusy] = useState(false);
  const [forwardErr, setForwardErr] = useState<string | null>(null);
  // Multi-select forward: the selected messages driving the shared picker
  // (null = closed), an in-flight guard, live per-file progress, a plain error
  // listing any files that failed, and a token bumped on FULL success (signals
  // the thread to exit select mode + clear its selection).
  const [forwardManyMsgs, setForwardManyMsgs] = useState<InboxMessage[] | null>(
    null,
  );
  const [forwardManyBusy, setForwardManyBusy] = useState(false);
  const [forwardManyErr, setForwardManyErr] = useState<string | null>(null);
  const [forwardManyProgress, setForwardManyProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [forwardManyToken, setForwardManyToken] = useState(0);
  useEffect(() => {
    if (deepLinkParams.get('picker') !== '1') return;
    setPickerForKey(deepLinkParams.get('c'));
    const next = new URLSearchParams(deepLinkParams);
    next.delete('picker');
    setDeepLinkParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (pickerForKey && activeKey && activeKey !== pickerForKey) {
      setPickerForKey(null);
    }
  }, [activeKey, pickerForKey]);

  // Desktop-notification stop-gap (R-D5) — initialised INSIDE the module
  // (no main.tsx edit). Per-user mute persists per device.
  const notifier = useMemo(() => desktopNotifier(), []);
  const [notifyEnabled, setNotifyEnabled] = useState(
    () => loadPrefs(myUid).enabled,
  );
  const [notifyPermission, setNotifyPermission] = useState(() =>
    notifier.permission(),
  );
  const escalationWatermark = useRef(0);
  const prevRowsRef = useRef<InboxConversation[] | null>(null);

  // WA_TEAMINBOX_SEAM — live contacts stream for sender identity resolution.
  // Reuses listenClients (already-streamed by the Clients screen in another
  // route), so this is a second subscriber on the same collection — no extra
  // Firestore billing concern (Firestore charges per-read-document, and both
  // subscribers share the same SDK-level snapshot cache in the same tab).
  const [contacts, setContacts] = useState<LiveClient[]>([]);
  useEffect(() => listenClients(setContacts), []);

  // Leads stream — feeds the AD-039 joined-lead chip + the "Add to existing →
  // Leads" picker tab. Defaults to the same listenLeads the Leads screen runs
  // (a second subscriber shares the SDK snapshot cache, no extra read
  // concern); the dev harness passes a fixture-backed leadsSource. A failure
  // leaves the tab empty / the chip absent.
  const [leads, setLeads] = useState<Lead[]>([]);
  useEffect(
    () =>
      leadsSource(setLeads, () => {
        /* best-effort — the Leads tab just shows empty on error */
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // EMAIL queue — live care@ triage records (channel:'email'). A failed
  // subscribe keeps the WhatsApp inbox fully functional (email is additive).
  //
  // RESILIENCE (Email-lens-shows-0 fix, 2026-06-20): a Firestore onSnapshot
  // listener is KILLED PERMANENTLY on its first error (e.g. a transient
  // permission-denied during the sign-in token/claims race) — it does NOT
  // auto-retry. The old onError was `() => setEmailDrafts([])`: it silently
  // emptied the lens AND let the dead listener stay dead, so the owner saw a
  // permanent "Email 0" for the whole page-load even though the rules allow
  // the read and the docs exist. (The WhatsApp lens recovered because it is a
  // re-running callable poll, not a single onSnapshot.) Fix: surface the real
  // error, do NOT wipe already-loaded drafts on a transient error, and
  // re-subscribe with capped backoff so the lens self-heals once claims settle.
  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const subscribe = () => {
      if (cancelled) return;
      unsub = emailSource(
        (items) => {
          attempt = 0; // a successful snapshot clears the backoff
          setEmailDrafts(items);
          setEmailErr(null);
        },
        (err) => {
          // Surface the REAL error (permission-denied vs index vs network)
          // instead of swallowing it — this is what made the bug invisible.
          // eslint-disable-next-line no-console
          console.error('[teaminbox] email queue listener error:', err);
          const e = err as { message?: string; code?: string } | null;
          const msg = e?.message || '';
          const code = e?.code || '';
          setEmailErr(
            /index/i.test(msg)
              ? 'Email inbox needs a Firestore index — it is building or not deployed yet. The list may be stale until it is ready.'
              : code === 'permission-denied' || /permission/i.test(msg)
                ? 'Email inbox is reconnecting… if it stays empty, refresh the page or re-sign-in (your session is still settling).'
                : 'Email inbox could not refresh — reconnecting. The list may be stale.',
          );
          // The listener is now dead. Re-subscribe with capped backoff; keep
          // whatever drafts we already had (don't flash to 0 on a blip).
          if (cancelled) return;
          if (unsub) {
            unsub();
            unsub = null;
          }
          attempt += 1;
          const delay = Math.min(1000 * 2 ** (attempt - 1), 30000);
          retryTimer = setTimeout(subscribe, delay);
        },
      );
    };

    subscribe();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (unsub) unsub();
    };
  }, [emailSource]);

  // HELD emails — a small recovery lane (best-effort; a failure just leaves
  // the held list empty, the active inbox is unaffected).
  useEffect(() => {
    const unsub = heldEmailSource(
      (items) => setHeldDrafts(items),
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[teaminbox] held-email listener error:', err);
      },
    );
    return () => unsub();
  }, [heldEmailSource]);

  // The merged identity: meta wins (server truth), prop is the boot value.
  const myId = meta?.me.staffId || myUid;
  const isAdmin = (meta?.me.role || role) === 'admin';

  // Canned messages this staff member may use in the Send-Picker (shared +
  // their own personal). Comes from server meta (settings/wa_canned echo).
  const visibleCannedList = useMemo(
    () => visibleCanned(meta?.canned || [], myId),
    [meta?.canned, myId],
  );

  // FIXED-VIEWPORT lock — REUSES the existing body.msgs-screen contract.
  useEffect(() => {
    document.body.classList.add('msgs-screen');
    return () => document.body.classList.remove('msgs-screen');
  }, []);

  const activeKeyRef = useRef<string | null>(null);
  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);

  // Number registry — live config; chips re-render on admin edits.
  useEffect(() => registrySource(setRegistry), [registrySource]);

  // Conversation list — onSnapshot-shaped subscription (poll inside).
  useEffect(
    () =>
      dataSource.subscribeList(
        (r) => {
          setRows((prev) => {
            // keep the optimistic unread-clear on the OPEN row between polls
            if (!prev) return r;
            return r.map((row) =>
              row.key === activeKeyRef.current ? { ...row, unread: 0 } : row,
            );
          });
          setListErr(null);
        },
        setListErr,
        () => registryRef.current,
      ),
    [dataSource],
  );

  // ── Phase-2 META poll: ownership/claim/escalations/teammates ──────
  // Rides the same cadence as the list poll, paused on hidden tabs.
  const rowKeys = useMemo(() => (rows || []).map((r) => r.key), [rows]);
  const rowKeysRef = useRef<string[]>([]);
  rowKeysRef.current = rowKeys;
  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    async function tick(initial: boolean) {
      if (stopped || inFlight) return;
      if (!initial && typeof document !== 'undefined' && document.hidden) {
        return;
      }
      if (rowKeysRef.current.length === 0 && !initial) return;
      inFlight = true;
      try {
        const m = await dataSource.fetchMeta(rowKeysRef.current);
        if (!stopped) setMeta(m);
      } catch {
        /* meta is an overlay — a failed poll keeps the last snapshot */
      } finally {
        inFlight = false;
      }
    }
    void tick(true);
    const id = window.setInterval(() => void tick(false), LIVE_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [dataSource]);

  // Merge meta over the mapped rows (owner chips / claim / escalation).
  const mergedRows = useMemo(
    () => (rows ? mergeMeta(rows, meta, Date.now()) : null),
    [rows, meta],
  );

  // ── desktop notifications: diff every merged snapshot ─────────────
  useEffect(() => {
    if (!mergedRows) return;
    const prev = prevRowsRef.current;
    prevRowsRef.current = mergedRows;
    if (!prev) {
      // First snapshot: just advance the escalation watermark silently.
      for (const e of meta?.escalations || []) {
        if (typeof e.ms === 'number') {
          escalationWatermark.current = Math.max(
            escalationWatermark.current,
            e.ms,
          );
        }
      }
      return;
    }
    const planRes = notificationPlan(prev, mergedRows, meta?.escalations || [], {
      myIds: [myId, myUid],
      role: isAdmin ? 'admin' : 'staff',
      adminScope: adminScopeOf(meta),
      escalationWatermarkMs: escalationWatermark.current,
    });
    escalationWatermark.current = planRes.escalationWatermarkMs;
    notifier.fire(planRes.items, {
      muted: !notifyEnabled,
      quietHours: quietHoursOf(meta),
      onOpen: (key) => openConversation(key),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedRows]);

  // Open / load ONE thread (also fired by the deep-linked initial key).
  const loadThread = useCallback(
    async (key: string, silent = false) => {
      if (!silent) {
        setThread(null);
        setEvents([]);
        setThreadLoading(true);
        setSendErr(null);
      }
      try {
        const [t, ev] = await Promise.all([
          dataSource.fetchThread(key),
          dataSource.fetchEvents(key).catch(() => [] as ThreadEvent[]),
        ]);
        if (activeKeyRef.current === key) {
          setThread(t);
          setEvents(ev);
        }
      } catch {
        if (!silent) setSendErr('Could not open that conversation.');
      } finally {
        if (!silent) setThreadLoading(false);
      }
    },
    [dataSource],
  );

  const openConversation = useCallback(
    (key: string | null) => {
      setActiveKey(key);
      onActiveChange?.(key);
      if (!key) {
        setThread(null);
        setEvents([]);
        return;
      }
      setRows((prev) => {
        const row = prev?.find((r) => r.key === key);
        setUnreadAtOpen(row?.unread || 0);
        return prev
          ? prev.map((r) => (r.key === key ? { ...r, unread: 0 } : r))
          : prev;
      });
      void dataSource.markRead(key);
      void loadThread(key);
    },
    [dataSource, loadThread, onActiveChange],
  );

  // Deep-linked key on mount.
  useEffect(() => {
    if (initialKey) openConversation(initialKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent re-poll of the OPEN thread so new inbounds appear live.
  useEffect(() => {
    const id = window.setInterval(() => {
      const k = activeKeyRef.current;
      if (!k) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      void loadThread(k, true);
    }, LIVE_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadThread]);

  // Self-upgrading snippet (§7.1 tick/media prefixes).
  useEffect(() => {
    if (!thread || thread.messages.length === 0) return;
    const last = thread.messages[thread.messages.length - 1];
    const snippet = snippetForMessage(last);
    setRows((prev) =>
      prev
        ? prev.map((r) => (r.key === thread.key ? { ...r, snippet } : r))
        : prev,
    );
  }, [thread]);

  const doSend = useCallback(
    async (text: string, attachment?: AttachmentInput): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      setSending(true);
      setSendErr(null);
      try {
        if (attachment) {
          await dataSource.sendAttachment(key, text || undefined, attachment);
        } else {
          await dataSource.sendText(key, text);
        }
        await loadThread(key, true);
        return true;
      } catch (e) {
        const msg = (e as { message?: string }).message || '';
        setSendErr(
          /send_blocked:window_closed_media_blocked/.test(msg)
            ? 'The 24-hour window closed while sending — a file cannot be ' +
              'sent outside it. Try again once the client messages.'
            : /send_blocked/.test(msg)
              ? 'WhatsApp blocked that send (the window closed). The message did not go out.'
              : 'Send failed — the message did not go out. Try again.',
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [dataSource, loadThread],
  );

  // Out-of-window: send ONE Meta-approved template (the only thing WhatsApp
  // allows once the 24h window is closed). Same single-message callable; the
  // server re-validates the template name against the approved allow-list.
  const doSendTemplate = useCallback(
    async (templateName: string, params: string[]): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      setSending(true);
      setSendErr(null);
      try {
        await dataSource.sendTemplate(key, templateName, params);
        await loadThread(key, true);
        return true;
      } catch (e) {
        const msg = (e as { message?: string }).message || '';
        setSendErr(
          /template_param_mismatch/.test(msg)
            ? 'That template needs all its blanks filled before it can go out. Fill them and try again.'
            : /send_blocked:not_approved|template_not_approved/.test(msg)
              ? 'That template is not approved for this number yet. Pick another or ask an admin.'
              : 'The template did not go out. Try again.',
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [dataSource, loadThread],
  );

  // Send ONE canned message — it's resolved free-form text, so it rides the
  // SAME in-window text path as a manual reply (no Meta approval; window-gated
  // server-side). The Send-Picker only offers canned when the window is open.
  const doSendCanned = useCallback(
    async (text: string): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key || !text.trim()) return false;
      setSending(true);
      setSendErr(null);
      try {
        await dataSource.sendText(key, text);
        await loadThread(key, true);
        return true;
      } catch (e) {
        const msg = (e as { message?: string }).message || '';
        setSendErr(
          /send_blocked/.test(msg)
            ? 'WhatsApp blocked that send (the window may have closed). Try a template.'
            : 'The message did not go out. Try again.',
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [dataSource, loadThread],
  );

  // AD-028 — react to / un-react from ONE message. The optimistic local pin
  // lives in <Thread/>; this just calls the backend and reports success so
  // <Thread/> can revert on failure. A silent (server-quiet) reload keeps the
  // authoritative reactions map in sync on the next poll anyway.
  const doReact = useCallback(
    async (targetWaMessageId: string, emoji: string): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      try {
        await dataSource.sendReaction(key, targetWaMessageId, emoji);
        return true;
      } catch {
        return false;
      }
    },
    [dataSource],
  );

  // FORWARD ONE message to a chosen chat/project. A message with a forwardable
  // attachment (image/document) is sent as a file — reusing the received bytes
  // via storagePath (no re-upload); a plain TEXT message is sent via sendText.
  // Refreshes the target thread when it is the one currently open, and surfaces
  // a plain error on failure. Never leaves the picker open on a doomed send.
  const doForward = useCallback(
    async (target: InboxConversation): Promise<void> => {
      const src = forwardMsg;
      if (!src || forwardBusy) return;
      const route = routeForward(toForwardItem(src));
      if (route.via === 'none') {
        setForwardErr('There is nothing to forward in this message.');
        return;
      }
      setForwardBusy(true);
      setForwardErr(null);
      try {
        if (route.via === 'attachment') {
          await dataSource.forwardAttachment(target.key, route.attachment);
        } else {
          await dataSource.sendText(target.key, route.text);
        }
        setForwardMsg(null);
        setForwardErr(null);
        // If we forwarded into the open thread, show it immediately.
        if (activeKeyRef.current === target.key) {
          await loadThread(target.key, true);
        }
      } catch (e) {
        const msg = (e as { message?: string }).message || '';
        if (route.via === 'text') {
          // Text-forward failure — plain, text-appropriate reason (WhatsApp
          // blocks free-text outside the 24h window too).
          setForwardErr(
            /send_blocked/.test(msg)
              ? "That chat's 24-hour window is closed, so WhatsApp won't " +
                'accept a new message there yet. Forward once they message, ' +
                'or send a template first.'
              : 'The message was not forwarded. Please try again.',
          );
        } else {
          setForwardErr(
            /forward_unavailable:no_source/.test(msg)
              ? 'This file can no longer be forwarded — the original is not ' +
                'reachable. Ask the sender to share it again.'
              : /send_blocked:window_closed_media_blocked|send_blocked/.test(msg)
                ? "That chat's 24-hour window is closed, so WhatsApp won't " +
                  'accept a file there. Forward once they message, or send a ' +
                  'template first.'
                : /unsupported_media_type/.test(msg)
                  ? "WhatsApp can't send this file type (e.g. SVG/EPS). Please " +
                    'convert it to PDF or PNG and try again.'
                  : 'The file was not forwarded. Please try again.',
          );
        }
      } finally {
        setForwardBusy(false);
      }
    },
    [dataSource, forwardMsg, forwardBusy, loadThread],
  );

  // MULTI-SELECT forward — loop the ONE-message transport once per selected
  // message to the chosen chat (the backend takes one per call — no bulk shape).
  // Each item is routed by `runForwardMany`: a file → `forwardAttachment`
  // (stored bytes, no re-upload); a text message → `sendText`. Shows per-item
  // progress; on ANY failure the picker stays open with a plain error naming
  // the item(s) that failed, and the selection is trimmed to just those so the
  // next pick retries only them. Closes + signals the thread to exit select
  // mode only when every item has landed.
  const doForwardMany = useCallback(
    async (target: InboxConversation): Promise<void> => {
      const msgs = forwardManyMsgs;
      if (!msgs || msgs.length === 0 || forwardManyBusy) return;
      const items: ForwardItem[] = msgs.map(toForwardItem);
      setForwardManyBusy(true);
      setForwardManyErr(null);
      setForwardManyProgress({ completed: 0, total: items.length });
      try {
        const outcome = await runForwardMany(
          items,
          {
            forwardAttachment: (att) =>
              dataSource.forwardAttachment(target.key, att),
            sendText: (text) => dataSource.sendText(target.key, text),
          },
          {
            onProgress: (completed, total) =>
              setForwardManyProgress({ completed, total }),
          },
        );
        // Refresh the target thread if it is the one on screen (show what landed).
        if (activeKeyRef.current === target.key) {
          await loadThread(target.key, true);
        }
        if (outcome.failed.length === 0) {
          // All landed → close, clear, and tell the thread to leave select mode.
          setForwardManyMsgs(null);
          setForwardManyErr(null);
          setForwardManyProgress(null);
          setForwardManyToken((t) => t + 1);
        } else {
          // Partial (or total) failure → keep the picker open, name the files
          // that failed, and narrow the retry set to just those. Nothing lost.
          const failedIds = new Set(outcome.failed.map((f) => f.id));
          setForwardManyMsgs(msgs.filter((m) => failedIds.has(m.id)));
          setForwardManyErr(forwardManyErrorText(outcome, items.length));
          setForwardManyProgress(null);
        }
      } catch (e) {
        // A rejection from the loop itself (not a per-file failure) — surface it
        // plainly and keep the picker open with the selection intact.
        setForwardManyErr(
          (e as { message?: string })?.message ||
            'The files were not forwarded. Please try again.',
        );
        setForwardManyProgress(null);
      } finally {
        setForwardManyBusy(false);
      }
    },
    [dataSource, forwardManyMsgs, forwardManyBusy, loadThread],
  );

  // ── ownership actions ─────────────────────────────────────────────
  const doAssign = useCallback(
    async (toStaffId: string, note: string): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      try {
        const r = await dataSource.assign(key, toStaffId, note);
        // Optimistic: reflect the new owner + the fresh system line now.
        setMeta((m) =>
          m
            ? {
                ...m,
                rows: {
                  ...m.rows,
                  [key]: {
                    ...(m.rows[key] || {
                      ownerName: null,
                      allocReason: null,
                      escalatedTier: 0,
                      claimedBy: null,
                      claimedByName: null,
                      claimedAtMs: null,
                    }),
                    ownerUid: r.ownerUid,
                    ownerName: r.ownerName,
                    claimedBy: null,
                    claimedByName: null,
                    claimedAtMs: null,
                  },
                },
              }
            : m,
        );
        void loadThread(key, true);
        return true;
      } catch {
        return false;
      }
    },
    [dataSource, loadThread],
  );

  const doClaim = useCallback(
    async (focused: boolean) => {
      const key = activeKeyRef.current;
      if (!key) return null;
      try {
        return await dataSource.claim(key, focused);
      } catch {
        return null;
      }
    },
    [dataSource],
  );

  const doAvailability = useCallback(
    (available: boolean) => {
      // Optimistic flip; the next meta poll confirms.
      setMeta((m) =>
        m ? { ...m, me: { ...m.me, available } } : m,
      );
      void dataSource.setAvailability(available).catch(() => {
        setMeta((m) => (m ? { ...m, me: { ...m.me, available: !available } } : m));
      });
    },
    [dataSource],
  );

  const enableNotify = useCallback(() => {
    void notifier.request().then((p) => {
      setNotifyPermission(p);
      if (p === 'granted') {
        setNotifyEnabled(true);
        savePrefs(myUid, { enabled: true });
      }
    });
  }, [notifier, myUid]);

  const toggleNotify = useCallback(
    (enabled: boolean) => {
      setNotifyEnabled(enabled);
      savePrefs(myUid, { enabled });
      if (enabled && notifier.permission() === 'default') enableNotify();
    },
    [myUid, notifier, enableNotify],
  );

  const saveRegistry = useCallback(
    async (doc: unknown) => {
      try {
        await dataSource.saveRegistry(doc);
        const m = await dataSource.fetchMeta(rowKeysRef.current);
        setMeta(m);
        return true;
      } catch {
        return false;
      }
    },
    [dataSource],
  );
  const saveCanned = useCallback(
    async (items: CannedReply[]) => {
      try {
        await dataSource.saveCanned(items);
        return true;
      } catch {
        return false;
      }
    },
    [dataSource],
  );

  // ── Module D: fetch older messages (thread scroll panel) ──────────
  // Thread.tsx is a presentation component with no direct store access,
  // so the callback is wired here and passed down as a prop.
  const doFetchOlderMessages = useCallback(
    (beforeMs: number) => {
      const key = activeKeyRef.current;
      if (!key) {
        return Promise.resolve({ messages: [], hasMore: false, nextCursor: null });
      }
      return dataSource.fetchOlderMessages(key, beforeMs);
    },
    [dataSource],
  );

  // ── M3.3: classification callbacks ───────────────────────────────
  const doConfirmProposal = useCallback(
    async (messageId: string, projectId: string, nodeType: string, projectIds: string[], taskTitle: string | null) => {
      await dataSource.confirmProposal(messageId, projectId, nodeType, projectIds, taskTitle);
      const key = activeKeyRef.current;
      if (key) void loadThread(key, true);
    },
    [dataSource, loadThread],
  );

  const doDismissProposal = useCallback(
    async (messageId: string, projectId: string) => {
      await dataSource.dismissProposal(messageId, projectId);
      const key = activeKeyRef.current;
      if (key) void loadThread(key, true);
    },
    [dataSource, loadThread],
  );

  const doCreateTask = useCallback(
    async (messageId: string, projectId: string, contactId: string, title: string) => {
      await dataSource.createTask(messageId, projectId, contactId, title);
      const key = activeKeyRef.current;
      if (key) void loadThread(key, true);
    },
    [dataSource, loadThread],
  );

  // Email rows mapped from the care@ triage queue, merged with WhatsApp. When
  // the "Held" toggle is on, the parked (held) emails are appended so they can
  // be reopened and un-held (recovery from an accidental Hold).
  const emailRows = useMemo(() => {
    const active = emailDrafts.map((d) =>
      draftToInboxConversation(d, openedEmailKeys),
    );
    if (!showHeld) return active;
    const held = heldDrafts.map((d) =>
      draftToInboxConversation(d, openedEmailKeys),
    );
    return [...active, ...held];
  }, [emailDrafts, heldDrafts, showHeld, openedEmailKeys]);
  const combinedRows = useMemo(
    () => [...(mergedRows || []), ...emailRows],
    [mergedRows, emailRows],
  );
  // Channel lens first, then the per-inbox (Number) filter — which folds in the
  // care@ email lane + the 3 WhatsApp numbers (item 5) — then the
  // All/Mine/Unassigned/Triage filters + search.
  const channelRows = useMemo(
    () => applyChannelFilter(combinedRows, channel),
    [combinedRows, channel],
  );
  const inboxRows = useMemo(
    () => channelRows.filter((c) => rowMatchesInbox(c, numberId)),
    [channelRows, numberId],
  );
  // Channel segment badge = TOTAL conversations per channel (read or unread).
  // Was UNREAD-only, which made "Email" show 0 once the emails had been read —
  // hiding a populated Email lens (P3 Phase-0 email-visibility fix). A total can
  // never read 0 while the list under it is populated.
  const chanCounts = useMemo(
    () => channelTotalCounts(combinedRows),
    [combinedRows],
  );
  // Per-inbox dropdown options (item 5) — over the full combined set.
  const inboxOptions = useMemo(
    () => inboxFilterOptions(registry, combinedRows),
    [registry, combinedRows],
  );
  const visible = useMemo(
    // numberId already applied above (rowMatchesInbox handles both lanes), so
    // pass 'all' here to avoid double-filtering.
    () => applyInboxFilter(inboxRows, filter, 'all', search, myId),
    [inboxRows, filter, search, myId],
  );
  const counts = useMemo(
    () => inboxFilterCounts(inboxRows, myId),
    [inboxRows, myId],
  );
  const activeFromList = combinedRows.find((r) => r.key === activeKey) || null;
  /**
   * A conversation can be OPEN but not on the loaded page — the list is
   * paginated by recency, so a deep link (?c=…) or "New WhatsApp message"
   * landing on an OLD existing thread would render "Pick a conversation" and
   * read as a dead click. The thread itself has already loaded by then, so
   * synthesize the row from it rather than showing nothing.
   */
  const active = useMemo(() => {
    if (activeFromList) return activeFromList;
    if (!activeKey || !thread || thread.key !== activeKey) return null;
    // The server ships the real row when the conversation is off the loaded
    // page — name, contact link, chips and all. Prefer it over anything we
    // could invent from the phone number alone.
    if (thread.offPageRow) return thread.offPageRow;
    const label = thread.phone || activeKey.replace(/^[a-z]+:/, '');
    const synthesized: InboxConversation = {
      key: activeKey,
      channel: 'whatsapp',
      name: label,
      phone: thread.phone,
      initials: label.slice(0, 2).toUpperCase(),
      snippet: '',
      lastMessageAt: thread.lastInboundMs,
      unread: 0,
      triage: activeKey.startsWith('triage:'),
      aiHandled: false,
      aiBot: false,
      aiPaused: false,
      projectId: null,
      projectRef: null,
      contactId: null,
      waProfileName: null,
      number: null,
      receivingPhoneNumberId: null,
    };
    return synthesized;
  }, [activeFromList, activeKey, thread]);
  const activeIsEmail = active?.channel === 'email';
  const replyFrom = active?.number?.line || null;
  const activeDraft = activeIsEmail
    ? emailDrafts.find((d) => d.id === activeKey) ||
      heldDrafts.find((d) => d.id === activeKey) ||
      null
    : null;
  const activeIsHeld = !!activeDraft && activeDraft.triageAction === 'hold';
  const actorId = myId || myUid || 'unknown';
  // The known contact behind a matched email sender (drives the email header's
  // "Known contact · C#####" identity). null when the sender is unmatched.
  const emailKnownContact = useMemo(() => {
    const cid = activeDraft?.knownContactId;
    if (!cid) return null;
    return contacts.find((c) => c.clientNo === cid || c.cid === cid) || null;
  }, [activeDraft, contacts]);

  // Channel-aware open: email rows are care@ triage records (no WA thread to
  // fetch) — open them locally and mark session-read; WA rows go through the
  // existing transport path.
  function handleOpen(key: string | null) {
    if (
      key &&
      (emailDrafts.some((d) => d.id === key) ||
        heldDrafts.some((d) => d.id === key))
    ) {
      setActiveKey(key);
      onActiveChange?.(key);
      setReplyErr(null);
      setOpenedEmailKeys((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      // Persist the read flag so the unread "1" badge stays cleared across
      // refreshes (best-effort — the optimistic openedKeys clear stands either
      // way). Held items have no badge but the write is harmless.
      void markEmailRead(key, actorId).catch(() => {
        /* best-effort; session clear already applied */
      });
      return;
    }
    openConversation(key);
  }

  // ── un-hold an accidentally-held email (recovery path) ────────────
  async function doUnhold(): Promise<void> {
    if (!activeDraft) return;
    setTriageBusy(true);
    try {
      await unholdDraftContact(activeDraft.id, actorId);
      // It rejoins the active queue; close the held pane.
      setActiveKey(null);
      onActiveChange?.(null);
    } catch {
      setReplyErr('Could not un-hold this email. Try again.');
    } finally {
      setTriageBusy(false);
    }
  }

  // ── email reply (sendEmailToClient, no-project) ───────────────────
  async function doReplyEmail(body: string): Promise<boolean> {
    if (!active?.email) return false;
    setReplying(true);
    setReplyErr(null);
    try {
      const res = await sendEmailReply(buildReplyParams(active.email, body));
      const key = active.key;
      const id = `sent-${(sentReplyCounter.current += 1)}`;
      const at = Date.now();
      setSentReplies((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), { id, at, body }],
      }));
      return !!res;
    } catch (e) {
      const msg = (e as { message?: string }).message || '';
      setReplyErr(
        msg.includes('failed-precondition')
          ? 'Email could not be sent — sender authentication for care@aayatanveda.in is not set up yet.'
          : 'The email did not go out. Try again.',
      );
      return false;
    } finally {
      setReplying(false);
    }
  }

  // ── email Archive / Hold (Create Lead/Contact open the FORM) ──────
  async function doTriageEmail(action: TriageAction): Promise<void> {
    if (!activeDraft) return;
    setTriageBusy(true);
    try {
      // Only archive/hold land here now — create flows go through the form.
      await setTriageAction(activeDraft.id, action, actorId);
      setActiveKey(null);
      onActiveChange?.(null);
    } catch {
      setReplyErr('That action did not complete. Try again.');
    } finally {
      setTriageBusy(false);
    }
  }

  // ── Create Contact / Create Lead FORM openers (item 2) ────────────
  // Email: pre-fill the sender email. WhatsApp: pre-fill the sender phone. The
  // NAME starts empty (the bug fix — it is no longer the phone number).
  function openCreateForm(
    mode: CreateMode,
    channel: 'whatsapp' | 'email',
    phone: string,
    email: string,
  ): void {
    setCreateForm({ mode, channel, phone, email });
  }
  function openEmailCreate(mode: CreateMode): void {
    if (!activeDraft) return;
    openCreateForm(
      mode,
      'email',
      activeDraft.phone || '',
      activeDraft.senderEmail || activeDraft.email || '',
    );
  }
  function openWaCreate(mode: CreateMode, phone: string): void {
    if (!phone) return;
    openCreateForm(mode, 'whatsapp', phone, '');
  }

  // Save the create form. Writes the TYPED name + editable identity, resolves
  // the canonical draft (email queue drops it; WA clears the conv-index flag).
  async function doSaveCreateForm(form: TriageFormInput): Promise<void> {
    if (!createForm) return;
    setCreateBusy(true);
    try {
      if (createForm.channel === 'email') {
        if (!activeDraft) return;
        if (createForm.mode === 'lead') {
          await createLeadFromForm(activeDraft, form, actorId);
        } else {
          await createContactFromForm(activeDraft, form, 'general', actorId);
        }
        setActiveKey(null);
        onActiveChange?.(null);
      } else {
        // WhatsApp — synthesise the canonical draft (same doc the router wrote).
        if (!active) return;
        const phone = createForm.phone;
        const draftId = waTriageDraftId(active.key, phone);
        const draft = buildWaTriageDraft({
          draftId,
          phone,
          firstMessage: active.snippet || '',
        });
        if (createForm.mode === 'lead') {
          // PROMOTE the router's holding lead when present (no duplicate); fall
          // back to the form's typed name + editable identity when none exists.
          await promoteWaLeadFromTriage(draft, actorId).catch(async () => {
            await createLeadFromForm(draft, form, actorId);
          });
        } else {
          await createContactFromForm(draft, form, 'general', actorId);
        }
        await dataSource
          .resolveTriage(
            active.key,
            createForm.mode === 'lead' ? 'create_lead' : 'create_contact',
          )
          .catch(() => {});
        openConversation(null);
      }
      setCreateForm(null);
    } catch {
      (createForm.channel === 'email' ? setReplyErr : setSendErr)(
        'Could not save. Try again.',
      );
    } finally {
      setCreateBusy(false);
    }
  }

  // ── "Add to existing" — open the two-tab picker (Contacts | Leads) ──
  function openEmailPicker(): void {
    if (!activeDraft) return;
    const identity = activeDraft.senderEmail || activeDraft.email || '';
    if (!identity) return;
    setPicker({ channel: 'email', identity, phone: null });
  }
  function openWaPicker(phone: string): void {
    if (!phone) return;
    setPicker({ channel: 'whatsapp', identity: phone, phone });
  }

  // Link the picked existing CONTACT: attach this channel's identity onto it
  // (server + client both auto-match future inbounds) and resolve the draft so
  // no duplicate lingers. A re-link is a harmless no-op + audit row.
  async function doLinkToExisting(client: LiveClient): Promise<void> {
    if (!picker) return;
    setLinkBusy(true);
    try {
      if (picker.channel === 'email') {
        if (!activeDraft) return;
        await linkEmailDraftToContact(activeDraft, client.clientNo, actorId);
        setActiveKey(null);
        onActiveChange?.(null);
      } else {
        await linkWhatsAppToContact(picker.identity, client.clientNo, actorId);
        await resolveWaTriage(active?.key || null, picker.phone, 'create_contact');
      }
      setPicker(null);
    } catch {
      setReplyErr('Could not link to that contact. Try again.');
    } finally {
      setLinkBusy(false);
    }
  }

  // Link the picked existing LEAD: route this channel identity to that lead
  // (item 3). Email resolves the draft (create_lead, linked); WhatsApp writes
  // the identity + clears the conv-index triage flag.
  async function doLinkToExistingLead(lead: Lead): Promise<void> {
    if (!picker) return;
    setLinkBusy(true);
    try {
      if (picker.channel === 'email') {
        if (!activeDraft) return;
        await linkEmailDraftToLead(activeDraft, lead, actorId);
        setActiveKey(null);
        onActiveChange?.(null);
      } else {
        await linkIdentityToLead(lead, 'whatsapp', picker.identity, actorId);
        await resolveWaTriage(active?.key || null, picker.phone, 'create_lead');
      }
      setPicker(null);
    } catch {
      setReplyErr('Could not link to that lead. Try again.');
    } finally {
      setLinkBusy(false);
    }
  }

  // ── WhatsApp triage resolution helper (AD-015, Email parity) ──────
  async function resolveWaTriage(
    convKey: string | null,
    phone: string | null,
    action: TriageAction,
  ): Promise<void> {
    const draftId = waTriageDraftId(convKey, phone);
    if (draftId) {
      await setTriageAction(draftId, action, actorId).catch(() => {
        /* the draft may not exist yet for a contentless first-touch — the
           conv-index clear below still removes the row from triage. */
      });
    }
    if (convKey) {
      await dataSource.resolveTriage(convKey, action).catch(() => {
        /* surfaced by the caller if it throws; best-effort here */
      });
    }
  }

  // Archive / Hold from an unknown WhatsApp sender (Create Lead/Contact go
  // through the form openers above).
  async function doTriageWa(action: TriageAction, phone: string): Promise<void> {
    if (!active) return;
    setTriageBusy(true);
    try {
      await resolveWaTriage(active.key, phone, action);
      openConversation(null);
    } catch {
      setSendErr('That action did not complete. Try again.');
    } finally {
      setTriageBusy(false);
    }
  }

  // ── email assign (real draft-doc write; LIVE) ─────────────────────
  async function doAssignEmail(staffId: string, name: string): Promise<boolean> {
    if (!activeDraft) return false;
    try {
      await assignDraftContact(activeDraft.id, staffId, name, actorId);
      return true;
    } catch {
      return false;
    }
  }

  // ── compose a brand-new outbound email from care@ ─────────────────
  async function doComposeSend(
    to: string,
    subject: string,
    body: string,
  ): Promise<boolean> {
    setComposeSending(true);
    setComposeErr(null);
    try {
      await sendNewEmail({ to, subject, body });
      return true;
    } catch (e) {
      const msg = (e as { message?: string }).message || '';
      setComposeErr(
        msg.includes('failed-precondition')
          ? 'Could not send — care@aayatanveda.in sender authentication is not set up yet.'
          : 'The email did not go out. Try again.',
      );
      return false;
    } finally {
      setComposeSending(false);
    }
  }

  /**
   * Open a conversation with someone who has never written to us, then hand
   * over to the thread itself.
   *
   * We do NOT send here. A first contact is always outside the 24-hour window,
   * so the thread composer lands on templates-only and the existing Send Picker
   * offers every approved template with its blanks and preview. One template UI,
   * not two.
   *
   * Assignment happens now rather than after the send, so the conversation is
   * owned the moment it exists — all four of us share one number, and an
   * unowned thread is one nobody answers.
   */
  async function doWaComposePick(phone: string, name: string): Promise<boolean> {
    setWaComposeSending(true);
    setWaComposeErr(null);
    try {
      const started = await startWaConversation({ phone, name });
      // Skip the assign when it is ALREADY mine — re-assigning writes another
      // "assigned to …" system line every time the conversation is opened.
      const myStaffId = meta?.me?.staffId;
      if (myStaffId && started.ownerUid !== myStaffId) {
        try {
          await dataSource.assign(
            started.conversationKey,
            myStaffId,
            'Opened this conversation',
          );
        } catch {
          /* cosmetic — the conversation is open either way */
        }
      }
      openConversation(started.conversationKey);
      return true;
    } catch (e) {
      const msg = (e as { message?: string }).message || '';
      setWaComposeErr(
        /country code/i.test(msg) || /invalid-argument/.test(msg)
          ? msg.replace(/^.*invalid-argument[:\s]*/i, '') ||
            'Check the number and try again.'
          : /permission-denied/.test(msg)
            ? 'You do not have access to message that number.'
            : 'Could not open the conversation. Nothing was sent — try again.',
      );
      return false;
    } finally {
      setWaComposeSending(false);
    }
  }

  // S6 registry/canned come from meta (server echo); the live registry
  // subscription still drives chips when meta has not arrived yet.
  const settingsMeta = meta;
  const notifyState: 'off' | 'on' | 'blocked' | 'ask' = !notifyEnabled
    ? 'off'
    : notifyPermission === 'granted'
      ? 'on'
      : notifyPermission === 'denied'
        ? 'blocked'
        : 'ask';

  return (
    <div className="ti-root">
      {listErr && (
        <div className="ti-send-err" role="alert">
          {listErr}
        </div>
      )}
      {emailErr && (
        <div className="ti-send-err" role="alert">
          {emailErr}
        </div>
      )}
      <div className={`ti${activeKey ? ' ti-has-active' : ''}`}>
        <InboxList
          loading={rows === null}
          rows={visible}
          allRows={mergedRows || []}
          registry={registry}
          filter={filter}
          numberId={numberId}
          search={search}
          activeKey={activeKey}
          myUid={myId}
          counts={counts}
          channel={channel}
          channelCounts={chanCounts}
          showHeld={showHeld}
          heldCount={heldDrafts.length}
          onToggleHeld={() => setShowHeld((v) => !v)}
          nowMs={Date.now()}
          available={meta ? meta.me.available : null}
          onAvailability={doAvailability}
          showSettings={can(meta, 'teaminbox.settings')}
          onOpenSettings={() => setSettingsOpen(true)}
          notifyState={notifyState}
          onEnableNotify={enableNotify}
          onFilter={setFilter}
          onChannel={setChannel}
          onComposeWa={() => {
            setWaComposeErr(null);
            setWaComposeOpen(true);
          }}
          onCompose={() => {
            setComposeErr(null);
            setComposeOpen(true);
          }}
          onNumber={setNumberId}
          inboxOptions={inboxOptions}
          onSearch={setSearch}
          onOpen={handleOpen}
          contacts={contacts}
          resolveContact={resolveContactByWhatsApp}
          onAddToExisting={openWaPicker}
        />
        {activeIsEmail ? (
          <EmailThread
            conversation={active}
            myId={myId}
            isAdmin={isAdmin}
            canAssign={can(meta, 'teaminbox.assign')}
            teammates={meta?.teammates || []}
            registry={registry}
            resolved={emailKnownContact}
            linkProject={linkProject}
            sentReplies={(active && sentReplies[active.key]) || []}
            replying={replying}
            replyErr={replyErr}
            triageBusy={triageBusy}
            onBack={() => handleOpen(null)}
            onReply={doReplyEmail}
            onTriage={doTriageEmail}
            onCreateLead={() => openEmailCreate('lead')}
            onCreateContact={() => openEmailCreate('contact')}
            onAddToExisting={openEmailPicker}
            onAssign={doAssignEmail}
            isHeld={activeIsHeld}
            onUnhold={doUnhold}
          />
        ) : (
          <Thread
            conversation={active}
            thread={thread}
            events={events}
            loading={threadLoading}
            sending={sending}
            sendErr={sendErr}
            unreadAtOpen={unreadAtOpen}
            replyFrom={replyFrom}
            teammates={meta?.teammates || []}
            myId={myId}
            isAdmin={isAdmin}
            canAssign={can(meta, 'teaminbox.assign')}
            registry={registry}
            linkProject={linkProject}
            onBack={() => openConversation(null)}
            initialPickerOpen={!!pickerForKey && activeKey === pickerForKey}
            onSend={doSend}
            onSendTemplate={doSendTemplate}
            onSendCanned={doSendCanned}
            canned={visibleCannedList}
            onReact={doReact}
            onForward={setForwardMsg}
            onForwardMany={(msgs) => setForwardManyMsgs(msgs)}
            forwardManyToken={forwardManyToken}
            forwardManyOpen={!!forwardManyMsgs && forwardManyMsgs.length > 0}
            onAssign={doAssign}
            onClaim={doClaim}
            contacts={contacts}
            resolveContact={resolveContactByWhatsApp}
            leads={leads}
            onAttachError={setSendErr}
            onFetchOlderMessages={doFetchOlderMessages}
            onConfirmProposal={doConfirmProposal}
            onDismissProposal={doDismissProposal}
            onCreateTask={doCreateTask}
            onAddToExisting={openWaPicker}
            onCreateContact={(phone) => openWaCreate('contact', phone)}
            onCreateLead={(phone) => openWaCreate('lead', phone)}
            onTriage={doTriageWa}
            triageBusy={triageBusy}
          />
        )}
      </div>
      {composeOpen && (
        <ComposeEmail
          sending={composeSending}
          err={composeErr}
          onClose={() => setComposeOpen(false)}
          onSend={doComposeSend}
        />
      )}
      {waComposeOpen && (
        <ComposeWhatsApp
          senderName={meta?.me?.name || 'Aayatan Veda'}
          contacts={contacts}
          busy={waComposeSending}
          err={waComposeErr}
          onClose={() => setWaComposeOpen(false)}
          onPick={doWaComposePick}
        />
      )}
      {createForm && (
        <CreateContactForm
          mode={createForm.mode}
          channel={createForm.channel}
          prefillPhone={createForm.phone}
          prefillEmail={createForm.email}
          busy={createBusy}
          onSave={(form) => void doSaveCreateForm(form)}
          onClose={() => setCreateForm(null)}
        />
      )}
      {picker && (
        <ContactPicker
          channel={picker.channel}
          identityLabel={picker.identity}
          contacts={contacts}
          leads={leads}
          busy={linkBusy}
          onPick={(c) => void doLinkToExisting(c)}
          onPickLead={(l) => void doLinkToExistingLead(l)}
          onClose={() => setPicker(null)}
        />
      )}
      {forwardMsg && (
        <ForwardPicker
          conversations={rows || []}
          fileName={forwardMsg.attachment?.fileName || null}
          filesReused={hasForwardableAttachment(forwardMsg)}
          excludeKey={activeKey}
          busy={forwardBusy}
          error={forwardErr}
          onPick={(target) => void doForward(target)}
          onClose={() => {
            setForwardMsg(null);
            setForwardErr(null);
          }}
        />
      )}
      {/* Multi-select forward — the SAME picker, driven by the selected set.
          Not shown at once with the single-file picker (distinct triggers). */}
      {forwardManyMsgs && forwardManyMsgs.length > 0 && (
        <ForwardPicker
          conversations={rows || []}
          fileName={forwardManyMsgs[0]?.attachment?.fileName || null}
          count={forwardManyMsgs.length}
          filesReused={forwardManyMsgs.some(hasForwardableAttachment)}
          excludeKey={activeKey}
          busy={forwardManyBusy}
          progress={forwardManyProgress}
          error={forwardManyErr}
          onPick={(target) => void doForwardMany(target)}
          onClose={() => {
            // Abandon the forward but KEEP the thread's selection (the thread
            // only clears on the success token) so the owner can adjust + retry.
            setForwardManyMsgs(null);
            setForwardManyErr(null);
            setForwardManyProgress(null);
          }}
        />
      )}
      {settingsOpen && settingsMeta && (
        <InboxSettings
          meta={settingsMeta}
          notifyEnabled={notifyEnabled}
          notifyPermission={notifyPermission}
          onToggleNotify={toggleNotify}
          onSaveRegistry={saveRegistry}
          onSaveCanned={saveCanned}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}

/* ───────────────────────── production wiring ───────────────────────── */

/**
 * The production Team Inbox: auth-derived identity (staffId — the id the
 * allocation engine writes; uid as defensive alias), live callable-backed
 * data source, `?c=<key>` deep-link persistence. Admin + Staff only —
 * defence in depth on top of the route gate and the server-side callable
 * visibility (locked conversation-visibility model).
 */
export default function TeamInbox() {
  const { role, user, staffId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const source = useMemo(() => liveDataSource(), []);

  const setKeyParam = useCallback(
    (key: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key) next.set('c', key);
          else next.delete('c');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  if (role !== 'admin' && role !== 'staff') {
    return (
      <Box title="WhatsApp Inbox" note="Restricted">
        <EmptyNote title="Not available on this track">
          The WhatsApp Team Inbox is a team operations tool.
        </EmptyNote>
      </Box>
    );
  }

  return (
    <TeamInboxView
      dataSource={source}
      myUid={staffId || user?.uid || null}
      role={role}
      initialKey={searchParams.get('c')}
      onActiveChange={setKeyParam}
    />
  );
}

/* ───────────────────────── flag gate ───────────────────────── */

/**
 * Dark-launch switch: renders the new Team Inbox ONLY when
 * settings/flags.teaminbox enables it for this user; otherwise the
 * `fallback` (today's screen) renders exactly as before. Default OFF —
 * a missing/malformed flag or read error always falls back.
 */
export function TeamInboxGate({ fallback }: { fallback: ReactNode }) {
  const { user } = useAuth();
  const [on, setOn] = useState(false);
  useEffect(
    () => subscribeTeamInboxFlag(user?.uid || null, setOn),
    [user?.uid],
  );
  return on ? <TeamInbox /> : <>{fallback}</>;
}
